import React, { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import cv from "./CV/Mirzaherenda.pdf";
import myPic from "./images/mypicture.jpg";
import {
  FaMapMarkerAlt,
  FaGithub,
  FaLinkedin,
  FaEnvelope,
  FaArrowRight,
  FaDownload,
} from "react-icons/fa";
import { motion } from "framer-motion";
import { Magnetic } from "./motion";

const stats = [
  { value: "2", label: "Apps shipped to stores" },
  { value: "4+", label: "Years building" },
  { value: "10", label: "Technologies" },
];

const socials = [
  { icon: FaGithub, href: "https://github.com/Mirza-herenda", label: "GitHub" },
  {
    icon: FaLinkedin,
    href: "https://www.linkedin.com/in/mirza-herenda-6710071a0/",
    label: "LinkedIn",
  },
  { icon: FaEnvelope, href: "mailto:herendamirza87@gmail.com", label: "Email" },
];

const GLOW_SHADOW = "0 0 30px -6px rgba(14,165,233,0.5)";

function HomePage({ isOpen, setIsOpen }) {
  const location = useLocation();
  const navigate = useNavigate();

  const glowRef = useRef(null);
  const sectionRef = useRef(null);
  const portraitRef = useRef(null);

  const closeModal = () => {
    setIsOpen(true);
  };

  function handleDownload() {
    const link = document.createElement("a");
    link.href = cv;
    link.download = "MIRZAHERENDACV.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // Mouse-follow glow, hero only.
  useEffect(() => {
    const section = sectionRef.current;
    const glow = glowRef.current;
    if (!section || !glow) return;
    if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches)
      return;

    let raf = 0;
    const onMove = (e) => {
      const r = section.getBoundingClientRect();
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        glow.style.opacity = "1";
        glow.style.transform = `translate3d(${e.clientX - r.left - 300}px, ${
          e.clientY - r.top - 300
        }px, 0)`;
      });
    };
    const onLeave = () => {
      glow.style.opacity = "0";
    };
    section.addEventListener("pointermove", onMove);
    section.addEventListener("pointerleave", onLeave);
    return () => {
      cancelAnimationFrame(raf);
      section.removeEventListener("pointermove", onMove);
      section.removeEventListener("pointerleave", onLeave);
    };
  }, []);

  // Very subtle portrait parallax.
  useEffect(() => {
    const el = portraitRef.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const y = Math.min(window.scrollY, 700) * 0.055;
        el.style.transform = `translate3d(0, ${y}px, 0)`;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative isolate overflow-hidden bg-dark-bg px-4 pt-32 pb-20 sm:px-6 sm:pt-40 lg:pb-32"
      onClick={closeModal}
    >
      {/* animated gradient field */}
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
        <div className="animate-drift absolute -top-40 -left-32 h-[46rem] w-[46rem] rounded-full bg-primary-500/16 blur-[130px]" />
        <div
          className="animate-drift absolute top-24 -right-40 h-[38rem] w-[38rem] rounded-full bg-sky-400/12 blur-[140px]"
          style={{ animationDelay: "-6s" }}
        />
        <div className="absolute inset-x-0 bottom-0 h-64 bg-gradient-to-b from-transparent to-dark-bg" />
        <div
          className="absolute inset-0 opacity-[0.18]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(30,41,59,0.8) 1px, transparent 1px), linear-gradient(90deg, rgba(30,41,59,0.8) 1px, transparent 1px)",
            backgroundSize: "72px 72px",
            maskImage:
              "radial-gradient(ellipse 70% 55% at 50% 35%, black, transparent)",
            WebkitMaskImage:
              "radial-gradient(ellipse 70% 55% at 50% 35%, black, transparent)",
          }}
        />
      </div>

      {/* mouse-follow glow */}
      <div
        ref={glowRef}
        aria-hidden
        className="pointer-events-none absolute top-0 left-0 -z-10 h-[600px] w-[600px] rounded-full opacity-0 blur-[110px] transition-opacity duration-500"
        style={{
          background:
            "radial-gradient(circle, rgba(14,165,233,0.22), transparent 65%)",
        }}
      />

      <div className="mx-auto grid max-w-6xl items-center gap-14 lg:grid-cols-[1.15fr_0.85fr] lg:gap-20 2xl:max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <h1 className="mt-7 text-[clamp(2.8rem,16vw,5.25rem)] sm:text-[clamp(2.6rem,7.2vw,5.25rem)] font-black leading-[0.94] tracking-tighter font-display">
            <span className="block text-transparent bg-gradient-to-r from-slate-50 to-slate-300 bg-clip-text">
              Mirza
            </span>
            <span className="block text-transparent bg-gradient-to-r from-primary-400 via-sky-300 to-primary-400 bg-clip-text">
              Herenda
            </span>
          </h1>

          <p className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-2 font-mono text-sm text-slate-400">
            <span className="text-slate-200">Software Developer</span>
            <span className="h-1 w-1 rounded-full bg-primary-400" />
            <span>React &amp; React Native</span>
            <span className="animate-pulse text-primary-400">_</span>
          </p>

          <p className="mt-7 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg">
            I design and build dynamic, responsive web and mobile products —
            from real-time analytics dashboards to streaming apps live on the
            App Store. Performance, detail and user experience come first.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Magnetic>
              <button
                onClick={() => navigate("/contact")}
                className="group inline-flex items-center gap-2 rounded-xl bg-primary-600 px-6 py-3.5 font-semibold text-white transition-shadow duration-300 hover:shadow-[0_0_30px_-6px_rgba(14,165,233,0.5)]"
                style={{ boxShadow: "none" }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.boxShadow = GLOW_SHADOW)
                }
                onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "none")}
              >
                Start a project
                <FaArrowRight className="text-sm transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </Magnetic>
            <Magnetic strength={0.18}>
              <button
                onClick={handleDownload}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-900/50 px-6 py-3.5 font-semibold text-white backdrop-blur-sm transition-colors duration-300 hover:border-primary-500/40 hover:bg-slate-800/60"
              >
                <FaDownload className="text-sm text-primary-400" />
                Download CV
              </button>
            </Magnetic>
          </div>

          <div className="mt-10 flex items-center gap-3">
            {socials.map(({ icon: Icon, href, label }) => (
              <a
                key={label}
                href={href}
                aria-label={label}
                target={href.startsWith("http") ? "_blank" : undefined}
                rel="noreferrer"
                className="group grid h-11 w-11 place-items-center rounded-xl border border-slate-800 bg-slate-900/40 transition-all duration-300 hover:-translate-y-0.5 hover:border-primary-500/45"
                onMouseEnter={(e) =>
                  (e.currentTarget.style.boxShadow = GLOW_SHADOW)
                }
                onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "none")}
              >
                <Icon className="text-[18px] text-slate-400 transition-colors duration-300 group-hover:text-primary-400" />
              </a>
            ))}
            <span className="ml-2 hidden items-center gap-1.5 text-xs text-slate-400 sm:inline-flex">
              <FaMapMarkerAlt className="text-primary-400" />
              Sarajevo, Bosnia &amp; Herzegovina
            </span>
          </div>

          <dl className="mt-12 grid max-w-lg grid-cols-3 gap-4 border-t border-slate-800 pt-8">
            {stats.map((stat) => (
              <div key={stat.label}>
                <dt className="text-2xl font-bold text-transparent bg-gradient-to-r from-primary-400 to-sky-400 bg-clip-text font-display sm:text-3xl">
                  {stat.value}
                </dt>
                <dd className="mt-1 text-xs leading-snug text-slate-500">
                  {stat.label}
                </dd>
              </div>
            ))}
          </dl>
        </motion.div>

        {/* portrait */}
        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          ref={portraitRef}
          className="relative mx-auto w-full max-w-[220px] xs:max-w-[260px] sm:max-w-xs lg:max-w-sm"
        >
          <div
            aria-hidden
            className="animate-float absolute -top-6 -left-6 h-20 w-20 rounded-2xl border border-primary-500/25 bg-primary-500/5 backdrop-blur-sm"
          />
          <div
            aria-hidden
            className="animate-float absolute -right-4 bottom-16 h-14 w-14 rounded-full border border-slate-800 bg-slate-900/60 backdrop-blur-sm"
            style={{ animationDelay: "-4s" }}
          />
          <div className="relative overflow-hidden rounded-3xl border border-slate-800 bg-slate-900/40 p-2 shadow-2xl backdrop-blur-md">
            <img
              src={myPic}
              alt="Portrait of Mirza Herenda"
              className="aspect-[4/5] w-full rounded-2xl object-cover"
            />
            <div
              aria-hidden
              className="pointer-events-none absolute inset-2 rounded-2xl"
              style={{
                background:
                  "linear-gradient(to top, rgba(15,23,42,0.85) 0%, transparent 45%)",
              }}
            />
            <div className="absolute inset-x-5 bottom-5 flex items-end justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-slate-100 font-display">
                  Currently studying
                </p>
                <p className="truncate text-xs text-slate-400">
                  International Burch University
                </p>
              </div>
              <span className="shrink-0 rounded-lg border border-primary-500/30 bg-primary-500/10 px-2.5 py-1 font-mono text-[10px] tracking-widest text-primary-400 uppercase">
                2026
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default HomePage;
